import { render } from 'preact-render-to-string';
import {
  BasicEmailWrapper,
  PrimaryAction,
  SupportFooter,
  Heading,
  Line,
  SubHeading,
  Blockquote,
  DoubleLine,
  Pill,
} from './shared';
import { AssignmentQuestionReview } from 'server/types';
import { Internationalize } from 'shared/intl';

type Props = {
  intl: Internationalize;
  ctaUrl: string;
  userName: string;
  guideName: string;
  courseName: string;
  questions: AssignmentQuestionReview[];
  hasRejected: boolean;
};

/**
 * Email to send student guide feedback on assignment
 */
export function AssignmentFeedback({
  intl,
  ctaUrl,
  userName,
  guideName,
  courseName,
  questions,
  hasRejected,
}: Props) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter />}>
      <Heading>{intl('You have received a review for your assignment.')}</Heading>
      <Line>{intl('Hi {userName:string},', { userName })}</Line>
      <Line>
        {intl(
          'You have just received a review from {guide:string} for your assignment on {courseName:string}.',
          {
            guide: guideName,
            courseName,
          },
        )}
      </Line>
      <div>
        {questions.map((q, index) => (
          <QuestionFeedbackItem key={`q-${index}`} intl={intl} {...q} />
        ))}
      </div>
      <PrimaryAction href={ctaUrl}>
        {hasRejected ? intl('Modify rejected answers') : intl('View assignment')}
      </PrimaryAction>
    </BasicEmailWrapper>,
  );
}

/**
 * Single row student answer with guide feedback and rejected/accepted status
 */
function QuestionFeedbackItem({
  intl,
  index,
  content,
  answer,
  feedback,
  status,
}: AssignmentQuestionReview & {
  intl: Internationalize;
}) {
  const isRejected = status === 'rejected';
  const labelStyle = 'margin-bottom:0.4rem;font-weight:600;color:#1e3a5f;';
  const answerBoxStyle = 'background-color:#f8fafc;border-color:#94a3b8;color:#334155;font-style:normal;';
  const feedbackBoxStyle = isRejected
    ? 'background-color:#fef2f2;border-color:#dc2626;color:#3f3f46;font-style:normal;'
    : 'background-color:#ecfdf5;border-color:#10b981;color:#3f3f46;font-style:normal;';
  return (
    <>
      <SubHeading>
        <strong>{intl('Question {index:string}.', { index: index.toString() })}</strong> {content}
      </SubHeading>
      <Line style={labelStyle}>{intl('Your answer:')}</Line>
      <Blockquote style={answerBoxStyle}>{answer}</Blockquote>
      {feedback && (
        <div style="margin-top:1.25rem;">
          <Line style={labelStyle}>{intl('Guide feedback:')}</Line>
          <Blockquote style={feedbackBoxStyle}>{feedback}</Blockquote>
        </div>
      )}

      <DoubleLine>
        {intl('Status:')}{' '}
        {isRejected ? (
          <Pill color="red">{intl('Rejected')}</Pill>
        ) : (
          <Pill color="green">{intl('Approved')}</Pill>
        )}
      </DoubleLine>
    </>
  );
}
